import React, { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { LabelDefinition } from '@/types/label-budget';

interface EditableColumnHeaderProps {
  labelDef: LabelDefinition;
  index: number;
  onEdit: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onSort: () => void;
  onReorder: (dragIndex: number, dropIndex: number) => void;
  sortKey: string;
  currentSortKey: string;
  sortDir: 'asc' | 'desc';
}

export const EditableColumnHeader: React.FC<EditableColumnHeaderProps> = ({
  labelDef,
  index,
  onEdit,
  onDelete,
  onSort,
  onReorder,
  sortKey,
  currentSortKey,
  sortDir
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(labelDef.name);
  const [editType, setEditType] = useState(labelDef.type);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLTableCellElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node) &&
          headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setShowTooltip(false);
        setIsEditing(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTooltip]);

  const handleSave = () => {
    if (editName.trim() && editName !== labelDef.name) {
      onEdit(labelDef.id, editName.trim());
    }
    setIsEditing(false);
    setShowTooltip(false);
  };

  const handleCancel = () => {
    setEditName(labelDef.name);
    setEditType(labelDef.type);
    setIsEditing(false);
  };

  const handleHeaderClick = (_e: React.MouseEvent) => {
    if (!showTooltip) {
      onSort();
    }
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowTooltip(true);
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    if (dragIndex !== index) {
      onReorder(dragIndex, index);
    }
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDragOverIndex(null);
  };

  return (
    <th 
      ref={headerRef}
      className={`px-3 py-1.5 text-left text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 relative group
        ${isDragging ? 'opacity-50' : ''}
        ${dragOverIndex === index ? 'bg-blue-100 dark:bg-blue-900/20' : ''}
      `}
      onClick={handleHeaderClick}
      onContextMenu={handleRightClick}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
    >
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2">
          <svg 
            className="w-3 h-3 text-gray-400 cursor-move"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
          {labelDef.name}
          {currentSortKey === sortKey && (
            <span className="ml-1">
              {sortDir === 'asc' ? '▲' : '▼'}
            </span>
          )}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowTooltip(!showTooltip);
          }}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
        </button>
      </div>

      {showTooltip && (
        <div
          ref={tooltipRef}
          className="absolute right-0 top-full mt-2 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl p-4 min-w-[250px]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="space-y-3">
            {/* Header */}
            <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-600 pb-2">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Column Settings
              </h4>
            </div>

            {/* Content */}
            {isEditing ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Column Name
                  </label>
                  <Input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full text-sm"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSave();
                      } else if (e.key === 'Escape') {
                        handleCancel();
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type
                  </label>
                  <select
                    value={editType}
                    onChange={(e) => setEditType(e.target.value as LabelDefinition['type'])}
                    className="w-full px-2 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded"
                    disabled
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="currency">Currency</option>
                    <option value="select">Select</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Type cannot be changed after creation
                  </p>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={handleCancel}
                    className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Name:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{labelDef.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Type:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {labelDef.type === 'text' && 'Text'}
                      {labelDef.type === 'number' && 'Number'}
                      {labelDef.type === 'currency' && 'Currency'}
                      {labelDef.type === 'select' && 'Select'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex-1 px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center gap-1"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit Name
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete column "${labelDef.name}"? This will remove all data in this column.`)) {
                        onDelete(labelDef.id);
                        setShowTooltip(false);
                      }
                    }}
                    className="px-3 py-1.5 text-xs bg-red-500 text-white rounded hover:bg-red-600 flex items-center justify-center gap-1"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-600">
                  <p>• Click header to sort</p>
                  <p>• Right-click to open this menu</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </th>
  );
};